import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Stack, TextField, useTheme } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SendIcon from "@mui/icons-material/Send";
import Typography from "components/basic/typography";
import { useSnack } from "hooks/useSnack";
import { convertHeicToJpeg, isHeic } from "utils/heic";

import {
  ACCEPTED_FILE_TYPES,
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_ATTACHMENT_BYTES,
  MAX_MESSAGE_LENGTH,
} from "../../../../constants";

interface ComposerProps {
  sending: boolean;
  // Resolves true when the message was sent; the composer then clears itself.
  onSend: (body: string, files: File[]) => Promise<boolean>;
}

const isAllowedType = (file: File) =>
  file.type === "application/pdf" ||
  file.type.startsWith("image/") ||
  isHeic(file);

const Composer: React.FC<ComposerProps> = ({ sending, onSend }) => {
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("messages");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [converting, setConverting] = useState(false);

  const canSend = (body.trim().length > 0 || files.length > 0) && !sending;

  const handleAddFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";

    const accepted: File[] = [];
    for (const file of picked) {
      if (!isAllowedType(file)) {
        showSnack(t("snack.type_not_allowed", { file: file.name }), "error");
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        showSnack(t("snack.too_large", { file: file.name }), "error");
        continue;
      }
      accepted.push(file);
    }

    if (accepted.some(isHeic)) {
      setConverting(true);
      try {
        for (let i = 0; i < accepted.length; i++) {
          accepted[i] = await convertHeicToJpeg(accepted[i]);
        }
      } finally {
        setConverting(false);
      }
    }

    setFiles((prev) => {
      const next = [...prev, ...accepted];
      if (next.length > MAX_ATTACHMENTS_PER_MESSAGE) {
        showSnack(
          t("snack.too_many", { max: MAX_ATTACHMENTS_PER_MESSAGE }),
          "error",
        );
      }
      return next.slice(0, MAX_ATTACHMENTS_PER_MESSAGE);
    });
  };

  const handleSend = async () => {
    if (!canSend) {
      return;
    }
    const sent = await onSend(body.trim(), files);
    if (sent) {
      setBody("");
      setFiles([]);
    }
  };

  return (
    <Box
      sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        px: 2,
        py: 1,
      }}
    >
      {files.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
          {files.map((file, idx) => (
            <Stack
              key={`${file.name}-${idx}`}
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                backgroundColor: theme.palette.action.hover,
                borderRadius: 1,
                px: 1,
                py: 0.25,
              }}
            >
              <InsertDriveFileIcon
                sx={{ fontSize: 14, color: theme.palette.action.active }}
              />
              <Typography variant="body2">{file.name}</Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setFiles((prev) => prev.filter((_, i) => i !== idx))
                }
                aria-label={t("composer.remove", { file: file.name })}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1} alignItems="flex-end">
        <IconButton
          component="label"
          disabled={sending || converting}
          aria-label={t("composer.attach")}
        >
          <AttachFileIcon />
          <input
            hidden
            type="file"
            multiple
            accept={ACCEPTED_FILE_TYPES}
            onChange={handleAddFiles}
          />
        </IconButton>

        <TextField
          fullWidth
          multiline
          maxRows={5}
          size="small"
          placeholder={t("composer.placeholder")}
          sx={{
            "& .MuiInputBase-input": {
              // iOS Safari zooms the page when a focused input renders under
              // 16px, and `size="small"` is 0.875rem.
              fontSize: { xs: 16, sm: 14 },
            },
          }}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />

        <IconButton
          color="primary"
          disabled={!canSend}
          onClick={handleSend}
          aria-label={t("composer.send")}
        >
          <SendIcon />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default Composer;
